import { petcoBrightGray2, petcoText, petcoWhite } from "@common/_assets/color-constants.js";
import { AppointmentListingModel, IHospitalHoursModel, ScheduleBlockTypeModel, schedule } from "@common/models";
import { AppointmentStatus, EntityScheduleType, ReservationAssignmentStatuses, ScheduleBlockType } from "@common/models/Enums";
import { AppointmentTypes } from "@common/models/Enums/AppointmentTypes";
import { BaseEntitySchedulePreference, HospitalScheduleVeterinarianModel, ScheduleHospitalScheduleTemplateListingModel } from "@common/models/Schedule";
import { EventInput } from "@fullcalendar/react";
import { ResourceInput } from '@fullcalendar/resource-common';
import { DateTime } from "luxon";

export const fullCalendarHoursOfOperationConverter = (hospitalHours: IHospitalHoursModel[] | undefined) => {
    const fullCalendarHospitalFormat = hospitalHours?.map((hospitalDay) => {
        const {
            openTime: startTime,
            closeTime: endTime,
            dayOfWeek,
            isOpen
        } = hospitalDay;
        if (isOpen) return {
            daysOfWeek: [dayOfWeek],
            startTime,
            endTime
        }
        return undefined;
    }).filter(x => x);
    return fullCalendarHospitalFormat;
}

export const hideSameDayReservationFromSameDay = (appointment: AppointmentListingModel, appointments: AppointmentListingModel[]) => {
    const foundRescheduledSameDay = appointments.find(appointmentSelf => {
        // 1) reschedule
        //    a) appointment changes status to rescheduled status
        //    b) appointment gets a rescheduleReservationId
        //    c) if appointments rescheduleReservationId matches another reservationId
        //    d) remove the item that had that rescheduledReservationId.
        return appointment.rescheduledReservationId === appointmentSelf.reservationId
    })
    const notSameDayReservation = (foundRescheduledSameDay) ? false : true;
    return notSameDayReservation;
}

export const hideSameDayReservationFromAllAppointments = (
    { appointment, appointments }:
        { appointment: AppointmentListingModel, appointments: AppointmentListingModel[] }
) => {
    const foundRescheduledSameDay = appointments.find(appointmentSelf => {
        // 1) reschedule
        //    a) appointment changes status to rescheduled status
        //    b) appointment gets a rescheduleReservationId
        //    c) if appointments rescheduleReservationId matches another reservationId
        //    d) remove the item that had that rescheduledReservationId.
        const rescheduleFound = appointment.rescheduledReservationId === appointmentSelf.reservationId
        const sameDay = appointment.startDate === appointmentSelf.startDate
        return rescheduleFound && sameDay ? true : false
    })

    const notSameDayReservation = (foundRescheduledSameDay) ? false : true;
    return notSameDayReservation;
}

export const makeBookingResource = (schedule: schedule.ProcessedHospitalScheduleModel, entity: schedule.HospitalScheduleDayEntityModel): ResourceInput | undefined => {
    const props: any = {}
    props.resourceType = entity.entityType;

    const vetOrTemplate = schedule.getScheduledEntity(entity);
    if (vetOrTemplate === undefined) return undefined;

    if (entity.entityType === EntityScheduleType.Veterinarian) {
        const vet = vetOrTemplate as HospitalScheduleVeterinarianModel;
        props.veterinarian = vet;
        props.sortText = vet.lastName + ',' + vet.firstName;
    } else if (entity.entityType === EntityScheduleType.Template) {
        const template = vetOrTemplate as ScheduleHospitalScheduleTemplateListingModel;
        props.template = template;
        props.sortText = template.name;
    } else {
        props.entityType = entity.entityType;
        props.sortText = entity.entityType === EntityScheduleType.Technician ? "Technician" : (entity.entityType === EntityScheduleType.Unassigned ? 'Unassigned' : 'Unknown');
    }

    const ret = {
        id: entity.uniqueId,
        title: entity.entityTitle,
        extendedProps: {
            ...props,
            resourceType: entity.entityType,
            entityType: entity.entityType,
            isAdHoc: entity.isAdhoc,
            order: entity.sortOrder,
            scheduleEntity: entity
        }
    }
    return ret;
}

export const createBackgroundSchedulePreferenceEvent = (selectedDate: DateTime, entity: schedule.HospitalScheduleDayEntityModel, pref: schedule.ComputedSchedulePreference, blockType: ScheduleBlockTypeModel | null | undefined): EventInput => {
    const dateString = selectedDate.toFormat('yyyy-LL-dd');
    const startTime = DateTime.fromFormat(`${dateString} ${pref.startTime}`, 'yyyy-LL-dd TT', { zone: 'UTC' });
    const endTime = DateTime.fromFormat(`${dateString} ${pref.endTime}`, 'yyyy-LL-dd TT', { zone: 'UTC' });
    const blocking = isSchedulePrefBlocking(pref);

    return {
        id: 'background_' + pref.entityPreferenceId,
        resourceId: entity.uniqueId.toString(),
        title: pref.scheduleBlockTypeName ?? "Unknown",
        allDay: false,
        textColor: petcoText,
        backgroundColor: "transparent",
        className: !blocking ? 'backgroundUnblocked' : 'backgroundBlocked',
        start: startTime.toISO(),
        end: endTime.toISO(),
        editable: false,
        startEditable: false,
        display: 'background',
        resourceEditable: false,
        overlap: !blocking,
        extendedProps: {
            eventType: 'background',
            schedulePreference: pref,
            scheduleBlockType: blockType
        }
    }
}

export const createAppointmentEvent = (appointment: AppointmentListingModel): EventInput => {
    let resourceId = "0";
    let unassignedResourceId = `0_${EntityScheduleType.Unassigned}`;
    let technicianResourceId = `-1_${EntityScheduleType.Technician}`;
    if (appointment.assignmentStatus !== undefined)
        switch (appointment.assignmentStatus) {
            case ReservationAssignmentStatuses.Unassigned:
                resourceId = unassignedResourceId;
                break;
            case ReservationAssignmentStatuses.Unassigned_Technician:
            case ReservationAssignmentStatuses.Assigned_Technician:
                resourceId = technicianResourceId;
                break;
            case ReservationAssignmentStatuses.Assigned:
                //resource id is entityId_entityTypeId, so need to add the type here, there can be resources with the same entity id, need type to make them unique.
                resourceId = appointment.veterinarianId ? appointment.veterinarianId.toString() + "_" + EntityScheduleType.Veterinarian.toString() : unassignedResourceId;
                break;
            case ReservationAssignmentStatuses.Assigned_Template:
                resourceId = appointment.hospitalScheduleTemplateId ? appointment.hospitalScheduleTemplateId.toString() + "_" + EntityScheduleType.Template.toString() : unassignedResourceId;
                break;
            default:
                throw 'Unknown assignment status';
        }

    return {
        id: `${appointment.reservationId}`,
        extendedProps: {
            eventType: 'appointment',
            appointment: appointment
        },
        resourceId: resourceId,
        title: appointment.appointmentTypeName,
        allDay: false,
        textColor: petcoText,
        editable: appointment.isBlockedFromReschedule(),
        display: appointment.shouldHide() ? "none" : "block",
        startEditable: appointment.isBlockedFromReschedule(),
        durationEditable: appointment.appointmentTypeName === AppointmentTypes["Vaccines + Preventative Services"] ? false : true,
        resourceEditable: appointment.isBlockedFromReschedule(),
        backgroundColor: AppointmentStatus.showAsDormant(appointment.appointmentStatusId) ? petcoBrightGray2 : petcoWhite,
        className: "appointmentEvent",
        borderColor: appointment.appointmentTypeColor,
        start: DateTime.fromISO(appointment.startTime, { zone: 'UTC' }).toISO(),
        end: DateTime.fromISO(appointment.endTime, { zone: 'UTC' }).toISO()
    }
}

export const isSchedulePrefBlocking = (schedulePref: schedule.ComputedSchedulePreference | BaseEntitySchedulePreference): boolean => {
    return schedulePref.scheduleBlockTypeId === ScheduleBlockType.Lunch ||
        schedulePref.scheduleBlockTypeId === ScheduleBlockType.Meeting ||
        schedulePref.scheduleBlockTypeId === ScheduleBlockType.Notes ||
        schedulePref.scheduleBlockTypeId === ScheduleBlockType.Event ||
        schedulePref.scheduleBlockTypeId === ScheduleBlockType.HolidayPTO ||
        schedulePref.scheduleBlockTypeId === ScheduleBlockType.Training;
}
